import { modelsApi } from '@/api/models.api';
import { projectsApi } from '@/api/projects.api';
import { sanitizeFileName } from '@/utils/validators';
import { type ReactNode, createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

export type UploadQueueStatus = 'uploading' | 'processing' | 'done' | 'error';

export interface UploadQueueEntry {
  id: string;
  title: string;
  fileNames: string[];
  status: UploadQueueStatus;
  progress: number;
  projectId?: string;
  /** Latest background task id for this upload (SSE); used for detail overlay / localStorage */
  taskId?: string;
  error?: string;
  cancel?: () => void;
}

export const projectTaskStorageKey = (projectId: string): string => `eda_task_${projectId}`;

function persistProjectTask(projectId: string, taskId: string): void {
  try {
    localStorage.setItem(projectTaskStorageKey(projectId), taskId);
  } catch (e) {
    console.warn('Failed to persist upload task id:', e);
  }
}

export function clearProjectTask(projectId: string): void {
  try {
    localStorage.removeItem(projectTaskStorageKey(projectId));
  } catch (e) {
    console.warn('Failed to clear upload task id:', e);
  }
}

function generateId(): string {
  return `upload-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function getUploadErrorMessage(error: unknown, projectId: string | null, t: (key: string) => string): string {
  let errorMessage = projectId ? t('projects.failedToUpload') : t('projects.failedToCreate');

  // APT generation failures carry an aggregated, user-meaningful message built on
  // the backend (see AptGenerationError). The taskiq wrapper prefixes it with
  // "Failed: "; strip that and surface the detail verbatim.
  if (error instanceof Error && error.message.includes('APT generation failed')) {
    return error.message.replace(/^Failed:\s*/, '');
  }

  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as {
      response?: { status?: number; data?: { detail?: string } };
      code?: string;
    };
    const status = axiosError.response?.status;
    const code = axiosError.code;
    const detail = axiosError.response?.data?.detail;

    if (code === 'ECONNREFUSED' || code === 'ENOTFOUND' || code === 'ERR_NETWORK' || code === 'ECONNABORTED') {
      errorMessage = t('projects.backendUnavailable');
    } else if (status === 400 && detail) {
      // Backend embedded-stock validation error (e.g. no "stock" solid, model
      // protrudes beyond stock AABB, unsupported colors) — surface verbatim.
      errorMessage = detail;
    } else if (status && status >= 500 && status < 600) {
      errorMessage = t('projects.serverError');
    } else if (status === 502 || status === 503 || status === 504) {
      errorMessage = t('projects.backendUnavailable');
    } else if (status === 401) {
      errorMessage = t('projects.authError');
    } else if (status === 404) {
      errorMessage = projectId ? t('projects.uploadError') : t('projects.failedToCreate');
    } else if (detail) {
      errorMessage = detail;
    }
  } else if (error instanceof Error) {
    const message = error.message.toLowerCase();
    if (
      message.includes('network') ||
      message.includes('fetch') ||
      message.includes('connection') ||
      message.includes('econnrefused') ||
      message.includes('enotfound') ||
      message.includes('err_network')
    ) {
      errorMessage = projectId ? t('projects.uploadConnectionError') : t('projects.createConnectionError');
    } else if (
      message.includes('500') ||
      message.includes('server') ||
      message.includes('internal') ||
      message.includes('502') ||
      message.includes('503') ||
      message.includes('504')
    ) {
      errorMessage = t('projects.serverError');
    } else if (message.includes('timeout') || message.includes('too long') || message.includes('timed out')) {
      errorMessage = projectId ? t('projects.uploadTimeout') : t('projects.createTimeout');
    } else if (message.includes('401') || message.includes('unauthorized') || message.includes('authentication')) {
      errorMessage = t('projects.authError');
    } else if (
      message.includes('sse') ||
      message.includes('stream') ||
      message.includes('connection lost') ||
      message.includes('server may be unavailable')
    ) {
      errorMessage = projectId ? t('projects.uploadConnectionErrorLong') : t('projects.createConnectionErrorLong');
    } else if (message.includes('upload failed') || message.includes('failed') || message.includes('not responding')) {
      errorMessage = t('projects.uploadFailed');
    }
  }

  return errorMessage;
}

export interface UploadQueueContextValue {
  uploads: UploadQueueEntry[];
  /** Create a project and upload a single embedded-stock STP file in the background. */
  startUpload: (file: File, refetch: () => Promise<void>, holeRuleSet?: string | null) => void;
  cancelUpload: (id: string) => void;
  getUploadForProject: (projectId: string) => UploadQueueEntry | undefined;
}

const UploadQueueContext = createContext<UploadQueueContextValue | null>(null);

export function UploadQueueProvider({ children }: { children: ReactNode }): React.ReactElement {
  const { t } = useTranslation();
  const [uploads, setUploads] = useState<UploadQueueEntry[]>([]);
  const uploadsRef = useRef(uploads);
  uploadsRef.current = uploads;

  const updateEntry = useCallback((id: string, update: Partial<UploadQueueEntry>) => {
    setUploads((prev) => prev.map((e) => (e.id === id ? { ...e, ...update } : e)));
  }, []);

  const removeEntry = useCallback((id: string) => {
    setUploads((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const startUpload = useCallback(
    (file: File, refetch: () => Promise<void>, holeRuleSet: string | null = null) => {
      const title = sanitizeFileName(file.name);
      const entryId = generateId();

      const entry: UploadQueueEntry = {
        id: entryId,
        title,
        fileNames: [file.name],
        status: 'uploading',
        progress: 0,
      };

      setUploads((prev) => [...prev, entry]);

      void (async () => {
        let projectId: string | null = null;
        const controller = new AbortController();
        let currentTaskId: string | null = null;

        try {
          // 1. Create project shell.
          const { uid } = await projectsApi.create({
            title,
            // biome-ignore lint/style/useNamingConvention: Backend API uses snake_case
            hole_rule_set: holeRuleSet,
          });
          projectId = uid;
          updateEntry(entryId, { projectId });

          // 2. Submit the embedded-stock upload.  The backend validates the STP
          //    synchronously here; a 400 means no "stock" solid was found and no
          //    background task started — in that case the empty project is dropped.
          let taskId: string;
          try {
            const cancel = async () => {
              controller.abort();
              if (currentTaskId) {
                try {
                  await modelsApi.cancelTask(currentTaskId);
                } catch (err) {
                  console.error('Failed to cancel task on backend:', err);
                }
              }
              clearProjectTask(projectId!);
            };
            updateEntry(entryId, { cancel, status: 'uploading', progress: 0 });

            const { uid: tid } = await modelsApi.uploadEmbeddedBackground(projectId, file);
            taskId = tid;
            currentTaskId = taskId;
          } catch (submitError) {
            // No background task was started, no model was saved — drop the empty project.
            await projectsApi.delete(projectId).catch(() => undefined);
            throw submitError;
          }

          persistProjectTask(projectId, taskId);
          updateEntry(entryId, { taskId });

          // 3. Track progress.  Failures past this point keep the project so the
          //    user can recover (re-upload, inspect partial state, etc.).
          await modelsApi.trackProgressSSE(
            taskId,
            (p) => {
              updateEntry(entryId, {
                progress: p.progress,
                status: p.progress < 10 ? 'uploading' : 'processing',
                taskId,
              });
            },
            controller.signal
          );

          clearProjectTask(projectId);
          updateEntry(entryId, { status: 'done', progress: 100, projectId, cancel: undefined });
          toast.success(t('projects.projectCreated'));
          await refetch();
          setTimeout(() => removeEntry(entryId), 1500);
        } catch (error) {
          if (projectId) {
            clearProjectTask(projectId);
          }
          if (error instanceof Error && error.name === 'AbortError') {
            updateEntry(entryId, { status: 'error', error: t('projects.uploadCancelled'), cancel: undefined });
            return;
          }
          console.error('Error during upload:', error);
          const errorMessage = getUploadErrorMessage(error, projectId, t);
          updateEntry(entryId, {
            status: 'error',
            error: errorMessage,
            cancel: undefined,
          });
          toast.error(errorMessage);
        }
      })();
    },
    [t, updateEntry, removeEntry]
  );

  const cancelUpload = useCallback((id: string) => {
    const entry = uploadsRef.current.find((e) => e.id === id);
    if (entry?.cancel) {
      void entry.cancel();
    }
  }, []);

  const getUploadForProjectStable = useCallback(
    (projectId: string) =>
      uploads.find((e) => e.projectId === projectId && (e.status === 'uploading' || e.status === 'processing')),
    [uploads]
  );

  const value = useMemo<UploadQueueContextValue>(
    () => ({
      uploads,
      startUpload,
      cancelUpload,
      getUploadForProject: getUploadForProjectStable,
    }),
    [uploads, startUpload, cancelUpload, getUploadForProjectStable]
  );

  return <UploadQueueContext.Provider value={value}>{children}</UploadQueueContext.Provider>;
}

export function useUploadQueueContext(): UploadQueueContextValue {
  const ctx = useContext(UploadQueueContext);
  if (!ctx) {
    throw new Error('useUploadQueueContext must be used within UploadQueueProvider');
  }
  return ctx;
}
